import { useEffect, useState } from "react";
import { useContent } from "@/context/ContentContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Briefcase, GraduationCap, Users, Truck, Award, Palette, Check } from "lucide-react";

const audiences = [
  { icon: Briefcase, title: "Corporate Teams", desc: "Team building & appreciation events." },
  { icon: GraduationCap, title: "Schools & Colleges", desc: "Programming & activations." },
  { icon: Users, title: "Nonprofits & Churches", desc: "Community engagement events." },
  { icon: Truck, title: "Mobile Events", desc: "We bring the studio to you." },
  { icon: Award, title: "Conference Activations", desc: "Memorable brand experiences." },
  { icon: Palette, title: "Brand Activations", desc: "Custom-branded creative stations." },
];

const EVENT_TYPES = [
  { id: "team_building", label: "Team-Building Workshop", desc: "Group experience at our studio or yours." },
  { id: "activation", label: "Corporate Activation / Large Event", desc: "Higher-volume events, rotating stations, brand activations." },
];

const EXPERIENCES = [
  { id: "rug_tufting", label: "Rug Tufting", rug: true },
  { id: "doormat", label: "Doormat Painting" },
  { id: "patch_hat", label: "Patch Hat Bar" },
  { id: "tuft_buddies", label: "Tuft Buddies" },
  { id: "custom", label: "Custom Creative Activation" },
];

export default function Corporate() {
  const pc = useContent().pages.corporate;
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({
    contact_name: "", organization: "", email: "", phone: "",
    event_type: "team_building",
    experience: "rug_tufting",
    event_date: "", event_time: "", location: "", mobile_location: "",
    attendance: 20, active_participants: 20,
    age_group: "Adults", rotate_stations: false,
    activity_time_per_participant: "",
    budget: "", branding_needs: "", setup_needs: "", experience_details: "", notes: "",
    rug_size_id: "",
  });
  const [sent, setSent] = useState(false);

  useEffect(() => {
    api.get("/booking-config").then((r) => {
      setCfg(r.data);
      const first = r.data.rug_sizes?.[0]?.id || "";
      setForm((f) => ({ ...f, rug_size_id: first }));
    });
  }, []);

  const isActivation = form.event_type === "activation";
  const experience = EXPERIENCES.find((e) => e.id === form.experience) || EXPERIENCES[0];
  const showRugSize = !!experience.rug;
  const showBrandingRotate = isActivation;
  const showMobileLocation = isActivation;

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/corporate-quotes", { ...form,
        attendance: Number(form.attendance) || 0,
        active_participants: Number(form.active_participants) || 0,
        experience: experience.label,
      });
      setSent(true);
      toast.success("Request sent — we'll follow up with a custom quote.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit request"); }
  };

  return (
    <div>
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="max-w-3xl">
          <div className="text-terracotta text-sm tracking-widest uppercase font-medium">{pc.eyebrow}</div>
          <h1 className="mt-2 font-serif font-bold text-5xl md:text-6xl leading-tight">{pc.heading}</h1>
          <p className="mt-4 text-lg text-charcoal/70">{pc.description}</p>
        </div>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {audiences.map((a, i) => (
            <div key={i} className="bg-white rounded-3xl p-8 shadow-card">
              <div className="w-12 h-12 rounded-full bg-forest/10 grid place-items-center"><a.icon size={22} className="text-forest" strokeWidth={1.5}/></div>
              <h3 className="mt-5 font-serif text-xl font-semibold">{a.title}</h3>
              <p className="mt-2 text-sm text-charcoal/70">{a.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 lg:px-8 py-16" id="quote">
        <div className="text-center mb-8">
          <div className="text-terracotta text-sm tracking-widest uppercase font-medium">Get a quote</div>
          <h2 className="mt-2 font-serif font-bold text-4xl md:text-5xl">Request a Corporate Quote</h2>
        </div>
        <div className="mx-auto max-w-3xl mb-8 rounded-2xl bg-ochre/10 border-2 border-ochre p-5 text-sm text-charcoal/85" data-testid="corp-custom-quote-notice">
          Corporate events are <b>custom quoted</b> based on group size, experience, event format, location, staffing, setup requirements, and customization. Submitting this form does not reserve a date — we'll follow up with a tailored quote for your review.
        </div>

        {sent ? (
          <div className="bg-white rounded-3xl p-10 md:p-14 shadow-card text-center max-w-3xl mx-auto" data-testid="corp-confirm">
            <div className="w-14 h-14 mx-auto rounded-full bg-forest/10 grid place-items-center text-forest"><Check size={28}/></div>
            <h3 className="mt-4 font-serif text-3xl font-semibold text-forest">Request received!</h3>
            <p className="mt-3 text-charcoal/70">Thanks — we'll follow up with a custom quote after we review your request.</p>
            <div className="mt-6 rounded-2xl bg-cream-alt p-5 text-left text-sm">
              <p><b>Next steps:</b></p>
              <ol className="mt-2 list-decimal list-inside space-y-1 text-charcoal/80">
                <li>We review your request and prepare a tailored quote.</li>
                <li>You approve the final event total.</li>
                <li>A <b>50% deposit</b> secures your date.</li>
                <li>The <b className="text-terracotta uppercase tracking-wide">remaining balance is due 7 days before the event</b>.</li>
              </ol>
            </div>
          </div>
        ) : !cfg ? <div className="text-center text-charcoal-muted">Loading…</div> : (
        <form onSubmit={submit} className="bg-white rounded-3xl p-8 shadow-card space-y-8 max-w-3xl mx-auto" data-testid="corporate-form">
          <Section title="Contact">
            <div className="grid md:grid-cols-2 gap-4">
              <F label="Contact Name *" v={form.contact_name} on={(v) => setForm({...form, contact_name: v})} req name="contact_name"/>
              <F label="Organization *" v={form.organization} on={(v) => setForm({...form, organization: v})} req name="organization"/>
              <F label="Work Email *" v={form.email} on={(v) => setForm({...form, email: v})} type="email" req name="email"/>
              <F label="Phone" v={form.phone} on={(v) => setForm({...form, phone: v})} name="phone"/>
            </div>
          </Section>

          <Section title="What type of corporate experience are you planning?">
            <div className="grid sm:grid-cols-2 gap-3" data-testid="corp-event-type">
              {EVENT_TYPES.map((t) => (
                <button key={t.id} type="button" onClick={() => setForm({...form, event_type: t.id})}
                  data-testid={`corp-event-type-${t.id}`}
                  className={`text-left rounded-2xl p-5 border-2 transition-colors ${form.event_type === t.id ? "border-terracotta bg-terracotta/5" : "border-cream-alt hover:border-terracotta/40"}`}>
                  <div className="font-serif text-lg font-semibold">{t.label}</div>
                  <div className="text-xs text-charcoal-muted mt-1">{t.desc}</div>
                </button>
              ))}
            </div>
          </Section>

          <Section title="Choose your experience">
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2" data-testid="corp-experience">
              {EXPERIENCES.map((e) => (
                <button key={e.id} type="button" onClick={() => setForm({...form, experience: e.id})}
                  data-testid={`corp-experience-${e.id}`}
                  className={`text-left rounded-2xl p-3 border-2 transition-colors ${form.experience === e.id ? "border-terracotta bg-terracotta/5" : "border-cream-alt hover:border-terracotta/40"}`}>
                  <div className="text-sm font-semibold">{e.label}</div>
                </button>
              ))}
            </div>
            <textarea data-testid="corp-experience-details" rows={2} placeholder="Any specific ideas or themes for this experience?"
              value={form.experience_details} onChange={(e) => setForm({...form, experience_details: e.target.value})}
              className="mt-3 w-full rounded-2xl border border-cream-alt px-4 py-3 focus:ring-2 focus:ring-terracotta focus:outline-none"/>

            {showRugSize && (
              <div className="mt-4" data-testid="corp-rug-size-block">
                <label className="text-sm font-medium">Preferred Rug Size (indicative only — used for planning)</label>
                <div className="mt-2 grid sm:grid-cols-3 gap-2">
                  {cfg.rug_sizes.map((s) => (
                    <button key={s.id} type="button" onClick={() => setForm({...form, rug_size_id: s.id})} data-testid={`corp-size-${s.id}`}
                      className={`text-left rounded-2xl p-3 border-2 transition-colors ${form.rug_size_id === s.id ? "border-terracotta bg-terracotta/5" : "border-cream-alt hover:border-terracotta/40"}`}>
                      <div className="font-medium">{s.label}</div>
                      <div className="text-xs text-charcoal-muted">{s.dimensions}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </Section>

          <Section title="Event details">
            <div className="grid md:grid-cols-2 gap-4">
              <F label="Preferred Date" v={form.event_date} on={(v) => setForm({...form, event_date: v})} type="date" name="event_date"/>
              <F label="Preferred Time" v={form.event_time} on={(v) => setForm({...form, event_time: v})} type="time" name="event_time"/>
              <F label="Age Group" v={form.age_group} on={(v) => setForm({...form, age_group: v})} name="age_group"/>
              <F label={isActivation ? "Expected Attendance" : "Group Size"} v={form.attendance} on={(v) => setForm({...form, attendance: v})} type="number" name="attendance"/>
              {isActivation && (
                <>
                  <F label="Estimated Active Participants" v={form.active_participants} on={(v) => setForm({...form, active_participants: v})} type="number" name="active_participants"/>
                  <F label="Activity Time per Participant" v={form.activity_time_per_participant} on={(v) => setForm({...form, activity_time_per_participant: v})} placeholder="e.g. 20 minutes" name="activity_time"/>
                </>
              )}
              <F label="Studio or Onsite Location" v={form.location} on={(v) => setForm({...form, location: v})} placeholder="'At the studio' or your address / city" name="location"/>
            </div>

            {showMobileLocation && (
              <div className="mt-4" data-testid="corp-mobile-location-block">
                <label className="text-sm font-medium">Mobile Event Location (if we're coming to you)</label>
                <textarea data-testid="corp-mobile-location" rows={2} value={form.mobile_location}
                  onChange={(e) => setForm({...form, mobile_location: e.target.value})}
                  placeholder="Venue, full address, city/state/ZIP, parking, access notes"
                  className="mt-1 w-full rounded-2xl border border-cream-alt px-4 py-3 focus:ring-2 focus:ring-terracotta focus:outline-none"/>
              </div>
            )}

            {showBrandingRotate && (
              <div className="mt-4 grid gap-3" data-testid="corp-activation-block">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" data-testid="corp-rotate" checked={form.rotate_stations} onChange={(e) => setForm({...form, rotate_stations: e.target.checked})}/>
                  Guests rotate through multiple creative stations
                </label>
                <div>
                  <label className="text-sm font-medium">Branding / Customization Needs</label>
                  <textarea data-testid="corp-branding" rows={2} value={form.branding_needs} onChange={(e) => setForm({...form, branding_needs: e.target.value})}
                    className="mt-1 w-full rounded-2xl border border-cream-alt px-4 py-3 focus:ring-2 focus:ring-terracotta focus:outline-none"/>
                </div>
                <div>
                  <label className="text-sm font-medium">Setup / Breakdown Needs</label>
                  <textarea data-testid="corp-setup" rows={2} value={form.setup_needs} onChange={(e) => setForm({...form, setup_needs: e.target.value})}
                    placeholder="Load-in windows, floor protection, tables/chairs available?, tear-down window"
                    className="mt-1 w-full rounded-2xl border border-cream-alt px-4 py-3 focus:ring-2 focus:ring-terracotta focus:outline-none"/>
                </div>
              </div>
            )}
          </Section>

          <Section title="Budget & notes">
            <div className="grid gap-3">
              <F label="Budget (optional)" v={form.budget} on={(v) => setForm({...form, budget: v})} placeholder="e.g. $2,500 – $5,000" name="budget"/>
              <div>
                <label className="text-sm font-medium">Additional Notes</label>
                <textarea data-testid="corp-notes" rows={3} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}
                  className="mt-1 w-full rounded-2xl border border-cream-alt px-4 py-3 focus:ring-2 focus:ring-terracotta focus:outline-none"/>
              </div>
            </div>
          </Section>

          <button data-testid="corp-submit" type="submit" className="w-full rounded-full bg-terracotta text-white px-8 py-4 font-semibold shadow-button">Request Custom Quote</button>
          <p className="text-xs text-charcoal-muted text-center">Submitting this form does not reserve a date. We'll email a custom quote for your review.</p>
        </form>
        )}
      </section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="font-serif text-xl font-semibold mb-3">{title}</div>
      {children}
    </div>
  );
}

function F({ label, v, on, type = "text", req, name, ...rest }) {
  return (
    <div>
      <label className="text-sm font-medium">{label}</label>
      <input data-testid={`corp-${name}`} required={req} type={type} value={v} onChange={(e) => on(e.target.value)} {...rest}
        className="mt-1 w-full rounded-2xl border border-cream-alt px-4 py-3 focus:ring-2 focus:ring-terracotta focus:outline-none"/>
    </div>
  );
}
